import { useEffect, useState } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/pagination';
import { twMerge } from 'tailwind-merge';

import prizePoolHighlightBackground from 'assets/single-event/prize-pool/prize_pool_list_item_bg.png';

import styles from '../../SingleEvent.module.scss';
import { ISingleEventFeatureHighlight, ISingleEventPrizePoolItem } from '../../types';

type PrizePoolItemProps = {
  image: string;
  name: string;
};

export const PrizePoolItem = ({ image, name }: PrizePoolItemProps) => {
  return (
    <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-2">
      <img src={image} alt={name} className="tw-h-[40px] tw-w-[40px] tw-object-contain" />
      <div className="tw-text-[24px] tw-font-semibold tw-text-white">{name}</div>
    </div>
  );
};

type PrizePoolHighlightItemProps = {
  image: string;
  name: string;
  value: string;
  label?: string;
};

const PrizePoolHighlightItem = ({ image, name, value, label = 'Prize pool USDT' }: PrizePoolHighlightItemProps) => {
  return (
    <div className="tw-relative tw-flex tw-flex-1 tw-flex-col tw-items-center">
      <img
        src={prizePoolHighlightBackground}
        alt={name}
        className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full tw-rounded-[16px] tw-object-cover"
      />
      <div className="tw-z-10 tw-flex tw-h-full tw-flex-col tw-items-center">
        <img src={image} alt={name} className="tw-absolute tw-top-[-43px] tw-h-[86px] tw-w-[86px]" />
        <div className="tw-mt-[43px] tw-px-[24px] tw-py-[14px] lg:tw-p-[24px]">
          <div className="tw-mb-[16px] tw-flex-1 tw-text-[22px] tw-font-bold tw-text-white lg:tw-text-[24px]">
            {name}
          </div>
          <div className="tw-text-[16px] tw-font-semibold tw-text-white lg:tw-text-[18px]">{label}</div>
          <div className="tw-text-[42px] tw-font-bold tw-text-turquoise lg:tw-text-[48px]">{value}</div>
        </div>
      </div>
    </div>
  );
};

type PrizePoolSectionProps = {
  prizePool: ISingleEventPrizePoolItem[];
  featureHighlights?: ISingleEventFeatureHighlight[];
  prizePoolHighlights?: ISingleEventFeatureHighlight[];
};

const PrizePoolSection = ({ prizePool, featureHighlights, prizePoolHighlights }: PrizePoolSectionProps) => {
  // Support both prop names for backwards compatibility
  const highlights = featureHighlights || prizePoolHighlights || [];
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-px-[20px] lg:tw-px-0">
      <h2 className={twMerge(styles.sectionHeader, 'tw-mb-[20px]')}>Prize Pool</h2>
      <div className="tw-w-full tw-rounded-[16px] tw-bg-blueCardToBottomGradient tw-p-[2px] lg:tw-w-auto">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-[20px] tw-rounded-[14px] tw-bg-[#1F408C] tw-px-[30px] tw-py-[15px] lg:tw-flex-row lg:tw-gap-[32px]">
          {prizePool.map((item, index) => {
            const isLastItem = index === prizePool.length - 1;

            return (
              <div key={item.name}>
                <PrizePoolItem image={item.image} name={item.name} />
                {!isLastItem && <div className="tw-h-[1px] tw-w-full tw-bg-[#3090DC] lg:tw-hidden" />}
              </div>
            );
          })}
        </div>
      </div>
      <div className="tw-h-[70px] lg:tw-h-[90px]" />
      {isMobile ? (
        <div className="tw-w-full tw-max-w-[1280px]">
          <Swiper
            // @ts-ignore
            modules={[Pagination]}
            spaceBetween={16}
            slidesPerView={1}
            pagination={{
              clickable: true,
            }}
            className={styles['swiper']}
          >
            {highlights.map((item) => (
              <SwiperSlide key={item.name}>
                <PrizePoolHighlightItem image={item.image} name={item.name} value={item.value} label={item.label} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        <div className="tw-flex tw-w-full tw-max-w-[1280px] tw-flex-row tw-gap-[16px]">
          {highlights.map((item) => (
            <PrizePoolHighlightItem
              key={item.name}
              image={item.image}
              name={item.name}
              value={item.value}
              label={item.label}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PrizePoolSection;
